import { computed, inject, Service, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ELocalStorage } from 'app/core/models/authentication/authentication';

export type ColorScheme = 'light' | 'dark' | 'auto';

@Service()
export class ThemeService {
    private readonly document = inject(DOCUMENT);
    private readonly _mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    public readonly scheme = signal<ColorScheme>(this._loadFromStorage());
    public readonly colorScheme = computed<'light' | 'dark'>(() =>
        this.scheme() === 'auto' ? (this._mediaQuery.matches ? 'dark' : 'light') : (this.scheme() as 'light' | 'dark'),
    );

    constructor() {
        this._apply(this.scheme());
        this._mediaQuery.addEventListener('change', () => {
            if (this.scheme() === 'auto') this._apply(this.scheme());
        });
    }

    setScheme(scheme: ColorScheme): void {
        this.scheme.set(scheme);
        localStorage.setItem(ELocalStorage.SCHEME, scheme);
        this._apply(scheme);
    }

    getResolvedScheme(): 'light' | 'dark' {
        return this.scheme() === 'auto' ? (this._mediaQuery.matches ? 'dark' : 'light') : (this.scheme() as 'light' | 'dark');
    }

    private _loadFromStorage(): ColorScheme {
        const loadScheme = localStorage.getItem(ELocalStorage.SCHEME);
        if (!loadScheme) {
            localStorage.setItem(ELocalStorage.SCHEME, 'light');
        }
        return (loadScheme as ColorScheme) ?? 'light';
    }

    private _apply(scheme: ColorScheme): void {
        const resolved = scheme === 'auto' ? (this._mediaQuery.matches ? 'dark' : 'light') : scheme;
        this.document.body.classList.remove('light', 'dark');
        this.document.body.classList.add(resolved);
    }
}
